import { z } from "@reflag/shared/utils/zod";

import { APIResponse } from "./api";
import { AppHeaderDTO } from "./appAPI";
import { LinearCleanupIssueDetails, PullRequest } from "./featureAPI";
import { AccessLevelSchema, Member, Org } from "./organizationAPI";
import { nameSchema } from "./schemas/nameSchema";

export type AdminOrgMember = Member & { isReflagAdmin: boolean };

export const CreateAppScenarios = ["new-demo"] as const;
export type DemoScenario = (typeof CreateAppScenarios)[number];

export type ScenarioOptions = {
  "new-demo": { userId: string };
};

export const AdminCreateOrgSchema = z
  .object({
    name: nameSchema,
  })
  .strict();

export type AdminCreateOrgType = z.infer<typeof AdminCreateOrgSchema>;

export const AdminPatchOrgSchema = z
  .object({
    accessLevel: AccessLevelSchema,
    trialEndsAt: z.string().datetime().nullable(),
    suspendedAt: z.string().datetime().nullable(),
    logoUrl: z.string().nullable(),
    isGlobal: z.boolean(),
    stripeCustomerId: z.string().trim().min(1).nullable(),
    stripeSubscriptionId: z.string().trim().min(1).nullable(),
    stripeSyncMtuPackagesMonthly: z.boolean(),
  })
  .partial();
export type AdminPatchOrgType = z.infer<typeof AdminPatchOrgSchema>;

export type StripeBillingInterval = "month" | "year";
export type StripeMtuLastSyncStatus = "success" | "failed" | "skipped";

export type AdminStripeBillingDetails = {
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  stripeSyncMtuPackagesMonthly: boolean;
  stripeCustomerDashboardUrl: string | null;
  stripeSubscriptionDashboardUrl: string | null;
  billingInterval: StripeBillingInterval | null;
  currentMonthMtu: number;
  previousMonthMtu: number;
  syncComputedPackageQuantity: number;
  checkoutComputedPackageQuantity: number;
  stripePackageQuantity: number | null;
  latestInvoiceStatus: string | null;
  latestInvoiceAmount: number | null;
  latestInvoiceCurrency: string | null;
  latestInvoiceUrl: string | null;
  drift: boolean | null;
  stripeError: string | null;
  stripeMtuLastSyncAt: string | null;
  stripeMtuLastSyncStatus: StripeMtuLastSyncStatus | null;
  stripeMtuLastSyncError: string | null;
  stripeMtuLastComputedQuantity: number | null;
  stripeMtuLastStripeQuantity: number | null;
};

export const AdminCreateDemoAppSchema = z
  .object({
    name: nameSchema,
    demoScenario: z.enum(CreateAppScenarios).optional(),
  })
  .strict();

export const AdminRunAppScenarioParamsSchema = z
  .object({
    orgId: z.string(),
    appId: z.string(),
  })
  .strict();

export const AdminRunAppScenarioQuerySchema = z
  .object({
    scenario: z.enum(CreateAppScenarios),
  })
  .strict();

export const AdminDeleteDemoAppParamsSchema = z
  .object({
    orgId: z.string(),
    appId: z.string(),
  })
  .strict();

export const AdminDeleteOrgParamsSchema = z
  .object({
    orgId: z.string(),
  })
  .strict();

export const AdminRemoveMemberParamsSchema = z
  .object({
    orgId: z.string(),
    memberId: z.string(),
  })
  .strict();

export const AdminDeleteUserParamsSchema = z
  .object({
    userId: z.string(),
  })
  .strict();

export const AdminSearchUsersQuerySchema = z
  .object({
    search: z.string().optional(),
  })
  .strict();

export type AdminUser = {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
};

export type AdminCreateDemoAppType = z.infer<typeof AdminCreateDemoAppSchema>;

export interface PullRequestLog {
  id: string;
  createdAt: string;
  message: string;
  metadata: Record<string, any> | null;
}

export type PullRequestDetails = PullRequest & {
  logs: PullRequestLog[];
};

export type AdminLinearCleanupIssue = {
  id: string;
  status: "creating" | "created" | "failed" | "skipped";
  title: string;
  url: string | null;
  details: LinearCleanupIssueDetails | null;
  createdAt: string;
  updatedAt: string;
  lastWebhookAt: string | null;
  deletedAt: string | null;
  warningCount: number;
  feature: {
    id: string;
    name: string;
    key: string | null;
    appName: string;
    url: string | null;
  };
};

export type AdminLinearCleanupIssueDetails = AdminLinearCleanupIssue & {
  description: string | null;
  templateId: string | null;
  templateName: string | null;
  teamId: string | null;
  projectId: string | null;
  ownerName: string | null;
  ownerEmail: string | null;
  assigneeId: string | null;
  delegateId: string | null;
  logs: {
    id: number;
    createdAt: string;
    level: string;
    message: string;
    metadata: Record<string, unknown> | null;
  }[];
};

export interface AdminAPI {
  "/admin/:orgId/linear-cleanup-issues": {
    GET: {
      params: { orgId: string };
      response: APIResponse<AdminLinearCleanupIssue[]>;
    };
  };
  "/admin/:orgId/linear-cleanup-issues/:issueId": {
    GET: {
      params: { orgId: string; issueId: string };
      response: APIResponse<AdminLinearCleanupIssueDetails>;
    };
  };
  "/admin": {
    POST: {
      response: APIResponse<Org>;
      body: AdminCreateOrgType;
    };
  };
  "/admin/:orgId": {
    PATCH: {
      params: {
        orgId: string;
      };
      body: AdminPatchOrgType;
      response: APIResponse<Org>;
    };
    DELETE: {
      params: z.infer<typeof AdminDeleteOrgParamsSchema>;
      response: object;
    };
  };
  "/admin/:orgId/apps": {
    POST: {
      params: {
        orgId: string;
      };
      body: AdminCreateDemoAppType;
      response: APIResponse<AppHeaderDTO>;
    };
  };
  "/admin/:orgId/apps/:appId": {
    DELETE: {
      params: z.infer<typeof AdminDeleteDemoAppParamsSchema>;
      response: object;
    };
  };
  "/admin/:orgId/members": {
    GET: {
      params: {
        orgId: string;
      };
      response: APIResponse<AdminOrgMember[]>;
    };
  };
  "/admin/:orgId/members/:memberId": {
    DELETE: {
      params: z.infer<typeof AdminRemoveMemberParamsSchema>;
      response: object;
    };
  };
  "/admin/:orgId/pull-requests": {
    GET: {
      params: {
        orgId: string;
      };
      response: APIResponse<PullRequest[]>;
    };
  };
  "/admin/:orgId/pull-requests/:pullId": {
    GET: {
      params: {
        orgId: string;
        pullId: string;
      };
      response: APIResponse<PullRequestDetails>;
    };
  };
  "/admin/:orgId/stripe-billing": {
    GET: {
      params: {
        orgId: string;
      };
      response: APIResponse<AdminStripeBillingDetails>;
    };
  };
  "/admin/:orgId/stripe-billing/sync": {
    POST: {
      params: {
        orgId: string;
      };
      response: APIResponse<AdminStripeBillingDetails>;
    };
  };
  "/admin/:orgId/code-references/sync": {
    POST: {
      params: {
        orgId: string;
      };
      response: APIResponse<null>;
    };
  };
  "/admin/code-references/sync-missing-hashes": {
    POST: {
      response: APIResponse<{
        queuedCount: number;
      }>;
    };
  };
  "/admin/users": {
    GET: {
      query: { search?: string };
      response: APIResponse<AdminUser[]>;
    };
  };
  "/admin/users/:userId": {
    DELETE: {
      params: { userId: string };
      response: object;
    };
  };
}
